import type { ChartDefinition } from '../../components/social-media/chart-types'

/**
 * The chart definitions the product dashboards draw.
 *
 * WHY THIS IS IN THE PAGES LAYER. Which breakdowns a department sees is an
 * OPINION — exactly the kind §5c says must be cheap to express and cheap to
 * diverge on. Putting the list in `components/socialdash/` would make it a
 * property of the kit, and the first department that wants a different chart
 * would be editing shared code. Here, a page that wants its own set writes its
 * own array and imports nothing from this file.
 *
 * NO DEFINITION CARRIES A VECTOR `filter`, and that is deliberate rather than an
 * omission. Each of these used to mirror its own side axis — `side:
 * 'sentiment(*)', filter: 'sentiment(*)'` — which did nothing: the Go engine
 * ACCEPTS a GlobalFilter only when it equals the side axis and then never
 * applies it, and the page's real filtering (campaign, platform, source, video
 * metrics, dates) travels in `labelQuery` and narrows the numerator AND the
 * denominator together. Verified live on bprsky/tiktok 2026-08-18: the response
 * is byte-identical with and without it, alone and under a combined filter.
 *
 * It was worse than inert. It read as meaningful, and it made two charts that
 * differ only in their side variable look like two different questions — which
 * is how the first attempt at batching them (T16) merged nothing at all. And if
 * a chart's driver were ever flipped to Carbon, the C# engine WOULD apply it,
 * restricting the colpc base to CODED comments instead of all of a department's
 * — the shape behind Sky's published "Sky Relevant 120.6% of 126 comments".
 *
 * So: a chart here filters through `labelQuery`, and carries a vector `filter`
 * only when it means something. The ones that do are "coded Y on framework X"
 * (`skyrelevant(1)`: sentiment among the comments coded Sky-relevant, #1489).
 * The Go engine applies that filter to the cells AND the column base, so the
 * percentage is of relevant comments, never of all comments. `useSkyCharts`
 * sends each filter as its own batch.
 *
 * EVERY DEFINITION CARRIES `driver: 'platinum'`. These queries are entity
 * filtered — that is what a product dashboard IS — and Carbon answers an
 * entity-filtered /spec/run with HTTP 200 and unfiltered numbers. The backend
 * forces the same value, so this is the second of two belts; both are here
 * because the failure is silent and renders one department's totals under
 * another's heading.
 */

/** The always-visible breakdowns: how this department's comments land. */
export const OVERVIEW_CHARTS: ChartDefinition[] = [
  {
    id: 'sentiment',
    title: 'Sentiment',
    carbon: { side: 'sentiment(*)', top: 'count(*)', driver: 'platinum' },
    graph: { chartType: 'bar', dataType: 'colPct' },
  },
  {
    id: 'emotion',
    title: 'Emotion',
    carbon: { side: 'emotion(*)', top: 'count(*)', driver: 'platinum' },
    // showDataLabels: Sky reads this chart to compare emotions against each
    // other, and asked for the figure on the bar rather than on hover
    // (#1201). It is set HERE, on the one definition that wants it, rather
    // than defaulted in the generator — which draws every clustered column
    // chart in Platinum. Sentiment deliberately does not carry it: the issue
    // named the emotion chart, and a chart of four bars reads fine without.
    graph: {
      chartType: 'clusteredColumn',
      dataType: 'colPct',
      switchAxis: true,
      showDataLabels: true,
    },
  },
]

/** The attribution pies — the two questions Sky's KPI tiles summarise. */
export const ATTRIBUTION_CHARTS: ChartDefinition[] = [
  {
    id: 'skyrelevant',
    title: 'Sky Relevant',
    carbon: { side: 'skyrelevant(*)', top: 'count(*)', driver: 'platinum' },
    graph: { chartType: 'pie', dataType: 'colPct' },
  },
  {
    id: 'skyattributed',
    title: 'Sky Attributed',
    carbon: { side: 'skyattributed(*)', top: 'count(*)', driver: 'platinum' },
    graph: { chartType: 'pie', dataType: 'colPct' },
  },
]

/** The vector filters for "the comments coded Yes on Sky Relevant / Sky Attributed" (Yes is code 1). */
export const RELEVANT_FILTER = 'skyrelevant(1)'
export const ATTRIBUTED_FILTER = 'skyattributed(1)'

/**
 * RELEVANT SENTIMENT % and ATTRIBUTED SENTIMENT % (Sky's row 2): the sentiment
 * of the comments coded Yes on Sky Relevant / Sky Attributed. The filter
 * narrows the column base too, so Positive reads "% of relevant comments".
 */
export const RELEVANCE_SENTIMENT_CHARTS: ChartDefinition[] = [
  {
    id: 'sentiment-relevant',
    title: 'Sentiment of Sky Relevant comments',
    carbon: { side: 'sentiment(*)', top: 'count(*)', filter: RELEVANT_FILTER, driver: 'platinum' },
    graph: { chartType: 'bar', dataType: 'colPct' },
  },
  {
    id: 'sentiment-attributed',
    title: 'Sentiment of Sky Attributed comments',
    carbon: { side: 'sentiment(*)', top: 'count(*)', filter: ATTRIBUTED_FILTER, driver: 'platinum' },
    graph: { chartType: 'bar', dataType: 'colPct' },
  },
]

/**
 * The measures over time. Titled "… over time" because they sit in the grid
 * with no section heading above them, and the Breakdowns section below carries
 * charts with the plain names. `month` is the only resolution the dashboards
 * offer today; the parameter exists because quarter and year are the obvious
 * next asks and this is where that answer belongs.
 *
 * Sky's row 5 is the two RELEVANCE-filtered sentiment charts; the Yes-share
 * charts of Sky Relevant and Sky Attributed live in Breakdowns.
 */
export const timeSeriesCharts = (period: 'year' | 'qtr' | 'month' = 'month'): ChartDefinition[] => {
  const top = `${period}(*)`
  return [
    {
      id: `sentiment-${period}`,
      title: 'Sentiment over time',
      carbon: { side: 'sentiment(*)', top, driver: 'platinum' },
      graph: { chartType: 'line', dataType: 'colPct' },
    },
    {
      id: `emotion-${period}`,
      title: 'Emotion over time',
      carbon: { side: 'emotion(*)', top, driver: 'platinum' },
      graph: { chartType: 'line', dataType: 'colPct' },
    },
    {
      id: `skyrelevant-${period}`,
      title: 'Sky Relevant over time',
      carbon: { side: 'skyrelevant(*)', top, driver: 'platinum' },
      graph: { chartType: 'line', dataType: 'colPct' },
    },
    {
      id: `skyattributed-${period}`,
      title: 'Sky Attributed over time',
      carbon: { side: 'skyattributed(*)', top, driver: 'platinum' },
      graph: { chartType: 'line', dataType: 'colPct' },
    },
    {
      id: `sentiment-relevant-${period}`,
      title: 'Relevant sentiment over time',
      carbon: { side: 'sentiment(*)', top, filter: RELEVANT_FILTER, driver: 'platinum' },
      graph: { chartType: 'line', dataType: 'colPct' },
    },
    {
      id: `sentiment-attributed-${period}`,
      title: 'Attributed sentiment over time',
      carbon: { side: 'sentiment(*)', top, filter: ATTRIBUTED_FILTER, driver: 'platinum' },
      graph: { chartType: 'line', dataType: 'colPct' },
    },
  ]
}

/**
 * RESPONSE OPINION SHARE and OPINIONS OVER TIME: what commenters think, from an
 * `opinion` framework coded on the comments. Sky has not agreed the list yet, so
 * no job codes one today, and the engine answers these two specs with a
 * per-spec error (an unknown side variable) that leaves the rest of the batch
 * alone. The page draws them only when a table with rows comes back and keeps
 * its placeholders otherwise, so adding the framework to the pipeline is all it
 * takes to switch them on.
 */
export const OPINION_SHARE_CHART: ChartDefinition = {
  id: 'opinion',
  title: 'Response opinion share',
  carbon: { side: 'opinion(*)', top: 'count(*)', driver: 'platinum' },
  // Counts, so each slice reads as its share of the opinions drawn. A column
  // percentage is of every comment in scope, and while opinion is coded on a
  // window only (a placeholder frame, a backfill to a cutoff) that read 0.1%
  // for the largest slice (21 Oct 2026).
  graph: { chartType: 'pie', dataType: 'freq' },
}

export const opinionsOverTimeChart = (period: 'year' | 'qtr' | 'month' = 'month'): ChartDefinition => ({
  id: `opinion-${period}`,
  title: 'Opinions over time',
  carbon: { side: 'opinion(*)', top: `${period}(*)`, driver: 'platinum' },
  graph: { chartType: 'line', dataType: 'colPct' },
})

/** Everything the standard arrangement fetches, in one array for the hook. */
export const STANDARD_CHARTS: ChartDefinition[] = [
  ...OVERVIEW_CHARTS,
  ...ATTRIBUTION_CHARTS,
  ...RELEVANCE_SENTIMENT_CHARTS,
  OPINION_SHARE_CHART,
  ...timeSeriesCharts('month'),
  opinionsOverTimeChart('month'),
]

/**
 * The post-tag label keys the OUTPUT OPINION SHARE pie can group by, in the
 * order the selector offers them. Sky has not said which tag they mean by
 * "output opinion", so the page offers all three and defaults to the first.
 */
export const OUTPUT_TAG_KEYS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'brand_campaign', label: 'Campaign tag' },
  { key: 'post_tag_lane', label: 'Lane tag' },
  { key: 'post_tag_flag', label: 'Flag tag' },
]

/**
 * The OUTPUT OPINION SHARE pie: what share of this department's POSTS carries
 * each value of a post tag.
 *
 * NOT IN STANDARD_CHARTS, and it must not be. Its data is not a crosstab:
 * `useSkyOutputOpinionShare` builds it from `/entities/metrics/query` grouped by
 * an entity label. The `carbon` block below is never sent anywhere; it exists
 * because `ChartDefinition` requires one, and it carries `driver: 'platinum'`
 * so that if someone does add it to the crosstab list, it cannot reach Carbon
 * and come back unfiltered.
 */
export const OUTPUT_SHARE_CHART: ChartDefinition = {
  id: 'output-share',
  title: 'Output opinion share',
  carbon: { side: 'output_share(*)', top: 'count(*)', driver: 'platinum' },
  // The column is "Share of output", which says nothing the title does not, and sat on a slice label.
  graph: { chartType: 'pie', dataType: 'colPct', hideColumnCaption: true },
}
